import { ConnectedPosition, Overlay, OverlayContainer, ScrollStrategy } from '@angular/cdk/overlay';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, forwardRef, HostListener, Input, OnChanges, OnDestroy, ViewChild } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { ScenarioDropdownGroup, ScenarioDropdownGroupView, ScenarioDropdownItem, ScenarioDropdownOption } from '../../models/scenario-dropdown.model';

let nextMenuId = 0;
/** Menus currently open inside a modal <dialog>; the shared overlay container returns to <body> when none remain. */
let dialogMenus = 0;

@Component({
  selector: 'app-scenario-dropdown',
  templateUrl: './scenario-dropdown.component.html',
  styleUrls: ['./scenario-dropdown.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [{ provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => ScenarioDropdownComponent), multi: true }],
})
export class ScenarioDropdownComponent implements ControlValueAccessor, OnChanges, OnDestroy {
  @Input() options: ScenarioDropdownOption[] = [];
  @Input() groups: ScenarioDropdownGroup[] = [];
  @Input() ariaLabel = 'Choose an option';
  @Input() placeholder = 'Choose an option';
  @ViewChild('trigger') trigger: ElementRef<HTMLButtonElement>;
  @ViewChild('menu') menu: ElementRef<HTMLElement>;

  readonly menuId = `sb-dropdown-${nextMenuId++}`;
  readonly positions: ConnectedPosition[] = [
    { originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top', offsetY: 4 },
    { originX: 'start', originY: 'top', overlayX: 'start', overlayY: 'bottom', offsetY: -4 },
  ];
  readonly scrollStrategy: ScrollStrategy;
  menuGroups: ScenarioDropdownGroupView[] = [];
  items: ScenarioDropdownItem[] = [];
  selectedLabel = 'Choose an option';
  selectedIconPath: string | undefined;
  hasSelection = false;
  open = false;
  disabled = false;
  menuWidth = 0;
  activeIndex = 0;
  private value: string | undefined;
  private inDialog = false;
  private typeahead = '';
  private typeaheadTimer: ReturnType<typeof setTimeout>;
  private onChange: (value: string | undefined) => void = () => undefined;
  private onTouched: () => void = () => undefined;

  constructor(overlay: Overlay, private readonly overlayContainer: OverlayContainer,
    private readonly host: ElementRef<HTMLElement>, private readonly cdr: ChangeDetectorRef) {
    this.scrollStrategy = overlay.scrollStrategies.reposition();
  }

  ngOnChanges(): void { this.refreshItems(); }

  writeValue(value: string | undefined): void {
    this.value = value;
    this.refreshItems();
  }

  registerOnChange(fn: (value: string | undefined) => void): void { this.onChange = fn; }
  registerOnTouched(fn: () => void): void { this.onTouched = fn; }
  setDisabledState(disabled: boolean): void {
    this.disabled = disabled;
    if (disabled) { this.close(false); }
    this.cdr.markForCheck();
  }

  toggle(): void { this.open ? this.close(true) : this.show(); }

  show(last = false): void {
    if (this.disabled || !this.items.length) { return; }
    this.menuWidth = this.trigger.nativeElement.getBoundingClientRect().width;
    const selected = this.items.findIndex(item => item.selected);
    this.activeIndex = selected >= 0 ? selected : last ? this.items.length - 1 : 0;
    // A modal <dialog> sits in the browser top layer, above the body-level CDK overlay container,
    // so host the container inside the dialog while this menu is open.
    const dialog = this.host.nativeElement.closest('dialog');
    if (dialog?.open && !this.inDialog) {
      this.inDialog = true;
      dialogMenus++;
      dialog.appendChild(this.overlayContainer.getContainerElement());
    }
    this.open = true;
    this.cdr.markForCheck();
  }

  attached(): void {
    Promise.resolve().then(() => { if (this.open) { this.focusItem(this.activeIndex); } });
  }

  close(restoreFocus: boolean): void {
    this.open = false;
    this.typeahead = '';
    clearTimeout(this.typeaheadTimer);
    if (this.inDialog) {
      this.inDialog = false;
      if (--dialogMenus === 0) { this.host.nativeElement.ownerDocument.body.appendChild(this.overlayContainer.getContainerElement()); }
    }
    this.onTouched();
    this.cdr.markForCheck();
    if (restoreFocus) { this.trigger?.nativeElement.focus(); }
  }

  choose(item: ScenarioDropdownItem): void {
    const changed = this.value !== item.id;
    this.value = item.id;
    this.refreshItems();
    if (changed) { this.onChange(item.id); }
    this.close(true);
  }

  triggerKey(event: KeyboardEvent): void {
    if (['ArrowDown', 'ArrowUp', 'Enter', ' '].includes(event.key)) {
      event.preventDefault();
      this.show(event.key === 'ArrowUp');
    }
  }

  menuKey(event: KeyboardEvent): void {
    if (event.key === 'Escape') { event.preventDefault(); event.stopPropagation(); this.close(true); return; }
    if (event.key === 'Tab') { this.close(true); return; }
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      this.focusItem((this.activeIndex + (event.key === 'ArrowDown' ? 1 : this.items.length - 1)) % this.items.length);
    } else if (event.key === 'Home' || event.key === 'End') {
      event.preventDefault();
      this.focusItem(event.key === 'Home' ? 0 : this.items.length - 1);
    } else if (event.key.length === 1 && !event.ctrlKey && !event.metaKey && !event.altKey && event.key !== ' ') {
      event.preventDefault();
      clearTimeout(this.typeaheadTimer);
      this.typeahead += event.key.toLocaleLowerCase();
      const term = new Set(this.typeahead).size === 1 ? this.typeahead[0] : this.typeahead;
      const start = term.length > 1 ? 0 : 1;
      for (let offset = start; offset < start + this.items.length; offset++) {
        const index = (this.activeIndex + offset) % this.items.length;
        if (this.items[index].label.toLocaleLowerCase().startsWith(term)) { this.focusItem(index); break; }
      }
      this.typeaheadTimer = setTimeout(() => this.typeahead = '', 700);
    }
  }

  trackGroup(index: number): number { return index; }
  trackItem(_index: number, item: ScenarioDropdownItem): number { return item.index; }
  @HostListener('window:resize')
  resized(): void { if (this.open) { this.close(false); } }
  ngOnDestroy(): void { if (this.open) { this.close(false); } clearTimeout(this.typeaheadTimer); }

  private focusItem(index: number): void {
    this.activeIndex = index;
    const item = this.menu?.nativeElement.querySelectorAll<HTMLButtonElement>('[role="option"]')[index];
    item?.focus();
    item?.scrollIntoView({ block: 'nearest' });
    this.cdr.markForCheck();
  }

  private refreshItems(): void {
    let index = 0;
    const groups: ScenarioDropdownGroup[] = this.groups.length ? this.groups : [{ options: this.options }];
    this.menuGroups = groups.map(group => ({ label: group.label, items: group.options.map(option => ({
      ...option, index: index++, selected: option.id === this.value,
    })) }));
    this.items = this.menuGroups.reduce((items, group) => items.concat(group.items), [] as ScenarioDropdownItem[]);
    const selected = this.items.find(item => item.selected);
    this.hasSelection = !!selected;
    this.selectedLabel = selected?.label || this.placeholder;
    this.selectedIconPath = selected?.iconPath;
    this.cdr.markForCheck();
  }
}
